import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Package, ArrowDownToLine, ArrowUpFromLine, Search, Camera, X, Loader2, LogOut, ChevronDown, Send, Trash2, ImagePlus, Plus, BarChart3 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StockTerminal() {
    const { companySlug } = useParams();
    const [step, setStep] = useState('pin');
    const [pin, setPin] = useState('');
    const [storedPin, setStoredPin] = useState('');
    const [checking, setChecking] = useState(false);
    const [worker, setWorker] = useState(null);
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [recentMoves, setRecentMoves] = useState([]);
    const [time, setTime] = useState(new Date());
    const inactivityRef = useRef(null);

    useEffect(() => {
        const t = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const handleLogout = useCallback(() => {
        setStep('pin'); setPin(''); setStoredPin(''); setWorker(null);
        setProducts([]); setWarehouses([]); setRecentMoves([]);
    }, []);

    const resetInactivity = useCallback(() => {
        if (inactivityRef.current) clearTimeout(inactivityRef.current);
        if (step === 'terminal') {
            inactivityRef.current = setTimeout(() => {
                handleLogout();
                toast.info('Inaktivite nedeniyle cikis yapildi');
            }, 5 * 60 * 1000);
        }
    }, [step, handleLogout]);

    useEffect(() => {
        if (step === 'terminal') {
            resetInactivity();
            const events = ['touchstart', 'mousedown', 'keydown'];
            events.forEach(e => document.addEventListener(e, resetInactivity));
            return () => {
                events.forEach(e => document.removeEventListener(e, resetInactivity));
                if (inactivityRef.current) clearTimeout(inactivityRef.current);
            };
        }
    }, [step, resetInactivity]);

    const handlePinDigit = (d) => { if (pin.length < 4) setPin(p => p + d); };
    const handlePinDelete = () => setPin(p => p.slice(0, -1));

    const handlePinSubmit = useCallback(async () => {
        if (pin.length !== 4) return;
        setChecking(true);
        try {
            const res = await axios.post(`${API}/api/stock-terminal/verify/${companySlug}`, { pin });
            setWorker(res.data);
            setStoredPin(pin);
            const [prodRes, whRes, recentRes] = await Promise.all([
                axios.get(`${API}/api/stock-terminal/products/${companySlug}`),
                axios.get(`${API}/api/stock-terminal/warehouses/${companySlug}`),
                axios.get(`${API}/api/stock-terminal/recent/${companySlug}/${res.data.employee_id}`),
            ]);
            setProducts(prodRes.data);
            setWarehouses(whRes.data);
            setRecentMoves(recentRes.data);
            setStep('terminal');
            setPin('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gecersiz PIN');
            setPin('');
        } finally { setChecking(false); }
    }, [pin, companySlug]);

    useEffect(() => { if (pin.length === 4) handlePinSubmit(); }, [pin, handlePinSubmit]);

    return (
        <div className="min-h-screen bg-[#F5F5F7] select-none" data-testid="stock-terminal" onContextMenu={e => e.preventDefault()}>
            <Toaster position="top-center" />
            {step === 'pin' && <PinScreen pin={pin} onDigit={handlePinDigit} onDelete={handlePinDelete} onSubmit={handlePinSubmit} checking={checking} time={time} />}
            {step === 'terminal' && worker && (
                <TerminalScreen
                    worker={worker} products={products} setProducts={setProducts}
                    warehouses={warehouses} recentMoves={recentMoves} setRecentMoves={setRecentMoves}
                    companySlug={companySlug} storedPin={storedPin}
                    onLogout={handleLogout} time={time}
                />
            )}
        </div>
    );
}

/* ───── PIN Screen ───── */
function PinScreen({ pin, onDigit, onDelete, onSubmit, checking, time }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-white">
            <div className="w-full max-w-xs">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 mb-5">
                        <Package size={22} className="text-[#3B82F6]" />
                        <span className="text-sm font-bold tracking-widest uppercase text-[#3B82F6]">Stok Terminal</span>
                    </div>
                    <div className="text-5xl font-extralight text-[#111] tracking-wider mb-1" data-testid="terminal-clock">
                        {time.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="text-xs text-[#999] mt-1">
                        {time.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                </div>
                <div className="flex justify-center gap-4 mb-3">
                    {[0,1,2,3].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full transition-all duration-200 ${i < pin.length ? 'bg-[#3B82F6] scale-125' : 'bg-[#D1D5DB]'}`} />
                    ))}
                </div>
                <p className="text-center text-[#999] text-xs mb-6">PIN kodunuzu girin</p>
                <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
                    {[1,2,3,4,5,6,7,8,9].map(d => (
                        <button key={d} onClick={() => onDigit(String(d))}
                            className="aspect-square rounded-2xl bg-[#F3F4F6] text-[#111] text-xl font-medium hover:bg-[#E5E7EB] active:bg-[#D1D5DB] active:scale-95 transition-all"
                            data-testid={`st-pin-${d}`}>{d}</button>
                    ))}
                    <button onClick={onDelete} className="aspect-square rounded-2xl bg-[#F9FAFB] text-[#9CA3AF] text-xs hover:bg-[#F3F4F6] active:scale-95 transition-all" data-testid="st-pin-delete">Sil</button>
                    <button onClick={() => onDigit('0')} className="aspect-square rounded-2xl bg-[#F3F4F6] text-[#111] text-xl font-medium hover:bg-[#E5E7EB] active:bg-[#D1D5DB] active:scale-95 transition-all" data-testid="st-pin-0">0</button>
                    <button onClick={onSubmit} disabled={pin.length !== 4 || checking}
                        className="aspect-square rounded-2xl bg-[#3B82F6] text-white text-xs font-semibold hover:bg-[#2563EB] active:scale-95 transition-all disabled:opacity-30 flex items-center justify-center"
                        data-testid="st-pin-submit">
                        {checking ? <Loader2 size={16} className="animate-spin" /> : 'Gir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ───── Terminal Screen ───── */
function TerminalScreen({ worker, products, setProducts, warehouses, recentMoves, setRecentMoves, companySlug, storedPin, onLogout, time }) {
    const [search, setSearch] = useState('');
    const [cart, setCart] = useState([]);
    const [moveType, setMoveType] = useState('IN');
    const [selectedWarehouse, setSelectedWarehouse] = useState(warehouses[0] || null);
    const [submitting, setSubmitting] = useState(false);
    const [isProduction, setIsProduction] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [showStockView, setShowStockView] = useState(false);
    const [stockSearch, setStockSearch] = useState('');
    const searchRef = useRef(null);
    const scannerInstanceRef = useRef(null);
    const scannerStoppedRef = useRef(false);

    // Flatten products + variants into searchable items
    const searchableItems = useMemo(() => {
        const items = [];
        for (const p of products) {
            const bpp = p.boxes_per_pallet || null;
            if (p.variants && p.variants.length > 1) {
                for (const v of p.variants) {
                    items.push({
                        product_id: p.id, product_name: p.name, reference: p.reference,
                        barcode: p.barcode, category: p.category,
                        variant_id: v.id, variant_name: v.name,
                        current_stock: v.current_stock ?? 0,
                        boxes_per_pallet: bpp,
                        key: p.id + v.id,
                        label: `${p.name} · ${v.name}`,
                        sub: p.reference || p.barcode || '',
                    });
                }
            } else {
                const v = p.variants?.[0];
                items.push({
                    product_id: p.id, product_name: p.name, reference: p.reference,
                    barcode: p.barcode, category: p.category,
                    variant_id: v?.id || null, variant_name: v?.name || null,
                    current_stock: v?.current_stock ?? p.current_stock ?? 0,
                    boxes_per_pallet: bpp,
                    key: p.id + (v?.id || ''),
                    label: v?.name ? `${p.name} · ${v.name}` : p.name,
                    sub: p.reference || p.barcode || '',
                });
            }
        }
        return items;
    }, [products]);

    const filteredItems = useMemo(() => {
        if (!search.trim()) return [];
        const q = search.toLowerCase();
        return searchableItems.filter(item =>
            item.label.toLowerCase().includes(q) ||
            (item.reference || '').toLowerCase().includes(q) ||
            (item.barcode || '').toLowerCase().includes(q)
        ).slice(0, 8);
    }, [search, searchableItems]);

    const noResults = search.trim().length >= 2 && filteredItems.length === 0;

    const addItemToCart = useCallback((item) => {
        setCart(prev => {
            const exists = prev.find(c => c.key === item.key);
            if (exists) {
                return prev.map(c => c.key === item.key ? { ...c, quantity: c.quantity + 1 } : c);
            }
            return [{
                key: item.key,
                product_id: item.product_id,
                product_name: item.product_name,
                reference: item.reference,
                variant_id: item.variant_id,
                variant_name: item.variant_name,
                current_stock: item.current_stock ?? 0,
                boxes_per_pallet: item.boxes_per_pallet || null,
                pallets: '',
                quantity: 1,
                notes: '',
            }, ...prev];
        });
        setSearch('');
        setShowSearch(false);
        toast.success(`${item.label} eklendi`);
    }, []);

    // Barcode scan handler
    const handleBarcodeScan = useCallback((barcode) => {
        const found = searchableItems.find(item =>
            item.barcode === barcode || item.reference === barcode
        );
        if (found) {
            addItemToCart(found);
        } else {
            toast.error(`"${barcode}" bulunamadi`);
            setSearch(barcode);
            setShowSearch(true);
            setShowQuickAdd(true);
        }
    }, [searchableItems, addItemToCart]);

    // Scanner effect - robust error handling
    useEffect(() => {
        if (!showScanner) {
            // Cleanup when hiding
            if (scannerInstanceRef.current && !scannerStoppedRef.current) {
                scannerStoppedRef.current = true;
                try { scannerInstanceRef.current.stop().catch(() => {}); } catch {}
                scannerInstanceRef.current = null;
            }
            return;
        }
        scannerStoppedRef.current = false;
        const timeoutId = setTimeout(() => {
            const el = document.getElementById("stock-terminal-scanner");
            if (!el || scannerStoppedRef.current) return;
            import('html5-qrcode').then(({ Html5Qrcode }) => {
                if (scannerStoppedRef.current) return;
                const scanner = new Html5Qrcode("stock-terminal-scanner");
                scannerInstanceRef.current = scanner;
                scanner.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 220, height: 220 } },
                    (text) => {
                        if (scannerStoppedRef.current) return;
                        scannerStoppedRef.current = true;
                        scanner.stop().catch(() => {}).finally(() => {
                            scannerInstanceRef.current = null;
                            setShowScanner(false);
                            handleBarcodeScan(text);
                        });
                    }
                ).catch(() => {
                    if (!scannerStoppedRef.current) toast.error('Kamera acilamadi');
                });
            }).catch(() => {});
        }, 100);
        return () => {
            clearTimeout(timeoutId);
            scannerStoppedRef.current = true;
            if (scannerInstanceRef.current) {
                try { scannerInstanceRef.current.stop().catch(() => {}); } catch {}
                scannerInstanceRef.current = null;
            }
        };
    }, [showScanner, handleBarcodeScan]);

    const toggleScanner = () => {
        if (showScanner) {
            scannerStoppedRef.current = true;
            if (scannerInstanceRef.current) {
                try { scannerInstanceRef.current.stop().catch(() => {}); } catch {}
                scannerInstanceRef.current = null;
            }
        }
        setShowScanner(prev => !prev);
    };

    const updateCartItem = (key, field, value) => {
        setCart(prev => prev.map(c => c.key === key ? { ...c, [field]: value } : c));
    };

    const removeFromCart = (key) => {
        setCart(prev => prev.filter(c => c.key !== key));
    };

    const validItems = cart.filter(c => c.quantity > 0);

    const handleSubmitAll = async () => {
        if (!selectedWarehouse) return toast.error('Depo secin');
        if (validItems.length === 0) return toast.error('Miktari 0\'dan buyuk en az bir urun ekleyin');
        setSubmitting(true);
        const results = [];
        try {
            for (const item of validItems) {
                const res = await axios.post(`${API}/api/stock-terminal/movement/${companySlug}`, {
                    pin: storedPin,
                    product_id: item.product_id,
                    variant_id: item.variant_id,
                    warehouse_id: selectedWarehouse.id,
                    movement_type: moveType,
                    quantity: item.quantity,
                    notes: item.notes || null,
                    is_production: isProduction,
                });
                results.push(res.data);
            }
            setRecentMoves(prev => [...results.reverse(), ...prev].slice(0, 30));
            toast.success(`${validItems.length} urun ${moveType === 'IN' ? 'giris' : 'cikis'} yapildi`);
            setCart([]);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Islem basarisiz');
        } finally { setSubmitting(false); }
    };

    const handleQuickAdd = async (name, reference, barcode, file) => {
        const fd = new FormData();
        fd.append('pin', storedPin);
        fd.append('name', name);
        fd.append('reference', reference || '');
        fd.append('barcode', barcode || '');
        if (file) fd.append('file', file);
        try {
            const res = await axios.post(`${API}/api/stock-terminal/quick-product/${companySlug}`, fd);
            const newProd = res.data;
            setProducts(prev => [newProd, ...prev]);
            // Add to cart
            addItemToCart({
                key: newProd.id,
                product_id: newProd.id,
                product_name: newProd.name,
                reference: newProd.reference,
                barcode: newProd.barcode,
                variant_id: null,
                variant_name: null,
                label: newProd.name,
            });
            setShowQuickAdd(false);
            toast.success(`${newProd.name} olusturuldu ve sepete eklendi`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Urun olusturulamadi');
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[#F5F5F7]">
            {/* Header */}
            <header className="flex items-center justify-between px-3 py-2.5 bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
                        <span className="text-xs font-bold text-[#3B82F6]">{worker.employee_name?.charAt(0)}</span>
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-[#111]" data-testid="worker-name">{worker.employee_name}</div>
                        <div className="text-[10px] text-[#9CA3AF] leading-tight">{worker.company_name}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setShowStockView(v => !v)}
                        className={`p-1.5 rounded-lg transition-colors ${showStockView ? 'bg-[#3B82F6]/10 text-[#3B82F6]' : 'text-[#9CA3AF] hover:bg-[#F3F4F6]'}`}
                        data-testid="terminal-stock-btn" title="Stok Durumu">
                        <BarChart3 size={16} />
                    </button>
                    <span className="text-[10px] text-[#9CA3AF]">{time.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    <button onClick={onLogout} className="p-1.5 rounded-lg hover:bg-[#FEE2E2] text-[#9CA3AF] hover:text-[#EF4444] transition-colors" data-testid="terminal-logout">
                        <LogOut size={16} />
                    </button>
                </div>
            </header>

            <main className="flex-1 px-3 py-3 max-w-lg mx-auto w-full space-y-3 pb-6">
                {/* Stock Overview Panel */}
                {showStockView && (
                    <StockOverview items={searchableItems} search={stockSearch} setSearch={setStockSearch} onClose={() => setShowStockView(false)} />
                )}

                {/* IN/OUT + Warehouse */}
                {!showStockView && (<>
                <div className="flex gap-2">
                    <div className="flex bg-white border border-[#E5E7EB] rounded-xl overflow-hidden flex-1">
                        <button onClick={() => setMoveType('IN')}
                            className={`flex-1 flex items-center justify-center gap-1.5 h-10 text-xs font-bold transition-all ${moveType === 'IN' ? 'bg-[#ECFDF5] text-[#059669]' : 'text-[#9CA3AF] hover:bg-[#F9FAFB]'}`}
                            data-testid="move-type-in">
                            <ArrowDownToLine size={14} /> GIRIS
                        </button>
                        <button onClick={() => setMoveType('OUT')}
                            className={`flex-1 flex items-center justify-center gap-1.5 h-10 text-xs font-bold transition-all ${moveType === 'OUT' ? 'bg-[#FEF2F2] text-[#DC2626]' : 'text-[#9CA3AF] hover:bg-[#F9FAFB]'}`}
                            data-testid="move-type-out">
                            <ArrowUpFromLine size={14} /> CIKIS
                        </button>
                    </div>
                    {warehouses.length > 1 && (
                        <div className="relative">
                            <select value={selectedWarehouse?.id || ''} onChange={e => setSelectedWarehouse(warehouses.find(w => w.id === e.target.value))}
                                className="h-10 pl-3 pr-7 bg-white border border-[#E5E7EB] rounded-xl text-xs text-[#111] outline-none appearance-none"
                                data-testid="terminal-warehouse-select">
                                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                        </div>
                    )}
                    <button onClick={() => setIsProduction(p => !p)}
                        className={`h-10 px-3 rounded-xl text-xs font-bold transition-all border ${isProduction ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-white text-[#9CA3AF] border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}
                        data-testid="terminal-production-toggle">
                        Uretim
                    </button>
                </div>

                {/* Search */}
                <div className="relative">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF] pointer-events-none" />
                            <input ref={searchRef} type="text" value={search}
                                onChange={e => { setSearch(e.target.value); setShowSearch(true); setShowQuickAdd(false); }}
                                onFocus={() => setShowSearch(true)}
                                placeholder="Urun, varyant, barkod ara..."
                                className="w-full h-10 pl-9 pr-8 bg-white border border-[#E5E7EB] rounded-xl text-sm text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#3B82F6] transition-colors"
                                data-testid="terminal-search" />
                            {search && (
                                <button onClick={() => { setSearch(''); setShowSearch(false); setShowQuickAdd(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#C5C5C7]">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <button onClick={toggleScanner}
                            className={`h-10 w-10 flex items-center justify-center rounded-xl border transition-all shrink-0 ${showScanner ? 'bg-[#3B82F6]/10 border-[#3B82F6]/30 text-[#3B82F6]' : 'bg-white border-[#E5E7EB] text-[#9CA3AF]'}`}
                            data-testid="terminal-scan-btn">
                            <Camera size={16} />
                        </button>
                    </div>

                    {/* Search Results - Each variant is a separate row */}
                    {showSearch && filteredItems.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl overflow-hidden max-h-60 overflow-y-auto shadow-lg z-20" data-testid="terminal-search-results">
                            {filteredItems.map(item => (
                                <button key={item.key} onClick={() => { addItemToCart(item); if (searchRef.current) searchRef.current.focus(); }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-[#F9FAFB] active:bg-[#F3F4F6] transition-colors text-left border-b border-[#F3F4F6] last:border-0">
                                    <Plus size={14} className="text-[#3B82F6] shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-[#111] truncate">{item.label}</div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            {item.sub && <span className="text-[10px] text-[#9CA3AF]">{item.sub}</span>}
                                            {item.boxes_per_pallet && <span className="text-[10px] text-purple-500">palet: {item.boxes_per_pallet}</span>}
                                        </div>
                                    </div>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${item.current_stock <= 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                                        {item.current_stock}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* No Results → Quick Add */}
                    {showSearch && noResults && !showQuickAdd && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl p-3 shadow-lg z-20">
                            <p className="text-xs text-[#9CA3AF] mb-2">"{search}" bulunamadi</p>
                            <button onClick={() => setShowQuickAdd(true)}
                                className="w-full h-9 flex items-center justify-center gap-1.5 bg-[#3B82F6] text-white text-xs font-semibold rounded-lg active:scale-[0.98] transition-all"
                                data-testid="quick-add-btn">
                                <Plus size={14} /> Hizli Urun Ekle
                            </button>
                        </div>
                    )}
                </div>

                {/* Scanner */}
                {showScanner && (
                    <div className="rounded-xl overflow-hidden border border-[#E5E7EB]">
                        <div id="stock-terminal-scanner" className="w-full" />
                    </div>
                )}

                {/* Quick Add Form */}
                {showQuickAdd && <QuickAddForm defaultRef={search} onAdd={handleQuickAdd} onCancel={() => setShowQuickAdd(false)} />}

                {/* Cart */}
                {cart.length > 0 && (
                    <div className="space-y-2" data-testid="cart">
                        <div className="flex items-center justify-between px-1">
                            <h3 className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">
                                Sepet ({cart.length} urun{validItems.length > 0 ? ` · ${validItems.length} aktif` : ''})
                            </h3>
                            <button onClick={() => setCart([])} className="text-[10px] text-[#DC2626] hover:underline">Temizle</button>
                        </div>
                        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#F3F4F6]">
                            {cart.map(item => (
                                <div key={item.key} className="px-3 py-2.5" data-testid={`cart-item-${item.key}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-[#111] truncate">
                                                {item.product_name}{item.variant_name ? <span className="text-[#6B7280] font-normal"> · {item.variant_name}</span> : ''}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                {item.reference && <span className="text-[10px] text-[#C5C5C7]">{item.reference}</span>}
                                                <span className={`text-[10px] font-semibold ${item.current_stock <= 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                    Stok: {item.current_stock}
                                                </span>
                                            </div>
                                        </div>
                                        <button onClick={() => removeFromCart(item.key)} className="p-1 rounded text-[#D1D5DB] hover:text-[#EF4444] transition-colors shrink-0">
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                    {/* Pallet input for products with boxes_per_pallet */}
                                    {item.boxes_per_pallet && (
                                        <div className="flex items-center gap-2 mb-2 bg-blue-50 rounded-lg px-2.5 py-1.5">
                                            <span className="text-[10px] text-blue-600 font-semibold shrink-0">Palet:</span>
                                            <input type="text" inputMode="numeric" pattern="[0-9]*"
                                                value={item.pallets}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    const p = parseInt(val) || 0;
                                                    const bpp = item.customBpp ?? item.boxes_per_pallet;
                                                    updateCartItem(item.key, 'pallets', val === '' ? '' : val);
                                                    if (p > 0) updateCartItem(item.key, 'quantity', p * bpp);
                                                }}
                                                placeholder="0"
                                                className="w-12 h-7 text-center text-sm font-bold text-blue-700 bg-white border border-blue-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                data-testid={`cart-pallet-${item.key}`} />
                                            <span className="text-[10px] text-blue-500 shrink-0">x</span>
                                            <input type="text" inputMode="numeric" pattern="[0-9]*"
                                                value={item.customBpp ?? item.boxes_per_pallet}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    const bpp = parseInt(val) || 0;
                                                    updateCartItem(item.key, 'customBpp', val === '' ? '' : bpp);
                                                    const p = parseInt(item.pallets) || 0;
                                                    if (p > 0 && bpp > 0) updateCartItem(item.key, 'quantity', p * bpp);
                                                }}
                                                className="w-12 h-7 text-center text-sm font-bold text-blue-700 bg-white border border-blue-200 rounded-lg outline-none focus:ring-1 focus:ring-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                data-testid={`cart-bpp-${item.key}`} />
                                            <span className="text-[10px] text-blue-500 shrink-0">koli</span>
                                            {item.pallets && parseInt(item.pallets) > 0 && (
                                                <span className="text-[10px] font-bold text-blue-700 ml-auto">= {parseInt(item.pallets) * (item.customBpp ?? item.boxes_per_pallet)}</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center bg-[#F3F4F6] rounded-lg overflow-hidden">
                                            <button onClick={() => updateCartItem(item.key, 'quantity', Math.max(0, item.quantity - 1))}
                                                className="w-9 h-9 flex items-center justify-center text-[#6B7280] text-base font-bold hover:bg-[#E5E7EB] active:bg-[#D1D5DB] transition-colors">-</button>
                                            <input type="text" inputMode="numeric" pattern="[0-9]*"
                                                value={item.quantity === 0 ? '' : item.quantity}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    if (val === '') { updateCartItem(item.key, 'quantity', 0); updateCartItem(item.key, 'pallets', ''); return; }
                                                    const n = parseInt(val);
                                                    if (!isNaN(n) && n >= 0) { updateCartItem(item.key, 'quantity', n); updateCartItem(item.key, 'pallets', ''); }
                                                }}
                                                onFocus={e => e.target.select()}
                                                className="w-12 h-9 text-center text-sm font-bold text-[#111] bg-white border-x border-[#E5E7EB] outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                data-testid={`cart-qty-${item.key}`} />
                                            <button onClick={() => updateCartItem(item.key, 'quantity', item.quantity + 1)}
                                                className="w-9 h-9 flex items-center justify-center text-[#6B7280] text-base font-bold hover:bg-[#E5E7EB] active:bg-[#D1D5DB] transition-colors">+</button>
                                        </div>
                                        <input type="text" value={item.notes} onChange={e => updateCartItem(item.key, 'notes', e.target.value)}
                                            placeholder="Not..."
                                            className="flex-1 h-9 px-2.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#3B82F6] min-w-0"
                                            data-testid={`cart-notes-${item.key}`} />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button onClick={handleSubmitAll} disabled={submitting || validItems.length === 0}
                            className={`w-full h-12 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed ${moveType === 'IN' ? 'bg-[#059669] text-white' : 'bg-[#DC2626] text-white'}`}
                            data-testid="terminal-submit-all">
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {submitting ? 'Gonderiliyor...' : `${validItems.length} URUN ${moveType === 'IN' ? 'GIRIS' : 'CIKIS'} YAP`}
                        </button>
                    </div>
                )}

                {/* Empty state */}
                {cart.length === 0 && !showSearch && !showQuickAdd && recentMoves.length === 0 && (
                    <div className="text-center py-12">
                        <div className="w-14 h-14 rounded-full bg-[#F3F4F6] flex items-center justify-center mx-auto mb-3">
                            <Package size={24} className="text-[#D1D5DB]" />
                        </div>
                        <p className="text-sm text-[#9CA3AF]">Urun arayarak sepete ekleyin</p>
                    </div>
                )}

                {/* Recent */}
                {recentMoves.length > 0 && (
                    <div data-testid="recent-moves">
                        <h3 className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5 px-1">Son Islemler</h3>
                        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#F3F4F6]">
                            {recentMoves.slice(0, 8).map((m, i) => (
                                <div key={m.id + '-' + i} className={`flex items-center gap-2.5 px-3 py-2 ${i === 0 ? 'bg-[#F0FDF4]' : ''}`} data-testid={`recent-move-${i}`}>
                                    <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${m.movement_type === 'IN' ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#FEF2F2] text-[#DC2626]'}`}>
                                        {m.movement_type === 'IN' ? <ArrowDownToLine size={11} /> : <ArrowUpFromLine size={11} />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs font-medium text-[#111] truncate">
                                            {m.product_name}{m.variant_name ? ` · ${m.variant_name}` : ''}{m.is_production ? <span className="ml-1 inline-flex items-center px-1 py-0 rounded text-[9px] font-semibold bg-amber-100 text-amber-700">Uretim</span> : ''}
                                        </div>
                                        <div className="text-[10px] text-[#C5C5C7]">
                                            {m.notes ? `${m.notes} · ` : ''}{m.created_at ? new Date(m.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                        </div>
                                    </div>
                                    <div className={`text-xs font-bold ${m.movement_type === 'IN' ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                                        {m.movement_type === 'IN' ? '+' : '-'}{m.quantity}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </>)}
            </main>
        </div>
    );
}

/* ───── Stock Overview Panel ───── */
function StockOverview({ items, search, setSearch, onClose }) {
    const filtered = search.trim()
        ? items.filter(it => {
            const q = search.toLowerCase();
            return it.label.toLowerCase().includes(q)
                || (it.reference || '').toLowerCase().includes(q)
                || (it.barcode || '').toLowerCase().includes(q)
                || (it.category || '').toLowerCase().includes(q);
        })
        : items;

    const totalStock = items.reduce((s, it) => s + (it.current_stock || 0), 0);

    return (
        <div className="space-y-3" data-testid="stock-overview">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#111] flex items-center gap-2">
                    <BarChart3 size={15} className="text-[#3B82F6]" />
                    Stok Durumu
                    <span className="text-[10px] font-medium text-[#9CA3AF] bg-[#F3F4F6] px-2 py-0.5 rounded-full">{items.length} urun · Toplam {totalStock.toLocaleString('de-DE')}</span>
                </h3>
                <button onClick={onClose} className="p-1 rounded-lg text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F4F6] transition-colors" data-testid="close-stock-view">
                    <X size={16} />
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Urun, referans, barkod ara..."
                    className="w-full h-10 pl-9 pr-8 bg-white border border-[#E5E7EB] rounded-xl text-sm text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#3B82F6] transition-colors"
                    data-testid="stock-overview-search" autoFocus />
                {search && (
                    <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#C5C5C7]">
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Product List */}
            <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#F3F4F6]">
                    {filtered.length === 0 ? (
                        <div className="p-6 text-center text-xs text-[#9CA3AF]">Sonuc bulunamadi</div>
                    ) : filtered.map(item => (
                        <div key={item.key} className="flex items-center gap-2.5 px-3 py-2.5" data-testid={`stock-item-${item.key}`}>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-[#111] truncate">{item.label}</div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    {item.reference && <span className="text-[10px] text-[#9CA3AF]">{item.reference}</span>}
                                    {item.category && <span className="text-[10px] text-[#C5C5C7]">{item.category}</span>}
                                </div>
                            </div>
                            <span className={`text-sm font-bold px-2.5 py-1 rounded-lg shrink-0 ${item.current_stock <= 0 ? 'bg-red-50 text-red-600' : item.current_stock < 10 ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-700'}`}
                                data-testid={`stock-qty-${item.key}`}>
                                {item.current_stock.toLocaleString('de-DE')}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ───── Quick Add Form ───── */
function QuickAddForm({ defaultRef, onAdd, onCancel }) {
    const [name, setName] = useState('');
    const [reference, setReference] = useState(defaultRef || '');
    const [barcode, setBarcode] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        const reader = new FileReader();
        reader.onload = (ev) => setPreview(ev.target.result);
        reader.readAsDataURL(f);
    };

    const handleSubmit = async () => {
        if (!name.trim()) return toast.error('Urun adi girin');
        setSaving(true);
        await onAdd(name.trim(), reference.trim(), barcode.trim(), file);
        setSaving(false);
    };

    return (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 space-y-3" data-testid="quick-add-form">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#111]">Hizli Urun Ekle</h3>
                <button onClick={onCancel} className="p-1 text-[#9CA3AF] hover:text-[#6B7280]"><X size={14} /></button>
            </div>

            {/* Photo */}
            <div className="flex items-center gap-3">
                <button onClick={() => fileRef.current?.click()}
                    className="w-16 h-16 rounded-xl border-2 border-dashed border-[#D1D5DB] flex items-center justify-center overflow-hidden shrink-0 hover:border-[#3B82F6] transition-colors"
                    data-testid="quick-add-photo-btn">
                    {preview ? (
                        <img src={preview} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <ImagePlus size={20} className="text-[#D1D5DB]" />
                    )}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
                <div className="flex-1 space-y-2">
                    <input type="text" value={name} onChange={e => setName(e.target.value)}
                        placeholder="Urun adi *"
                        className="w-full h-9 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-sm text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#3B82F6]"
                        data-testid="quick-add-name" autoFocus />
                </div>
            </div>

            <div className="flex gap-2">
                <input type="text" value={reference} onChange={e => setReference(e.target.value)}
                    placeholder="Referans"
                    className="flex-1 h-9 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#3B82F6]"
                    data-testid="quick-add-ref" />
                <input type="text" value={barcode} onChange={e => setBarcode(e.target.value)}
                    placeholder="Barkod"
                    className="flex-1 h-9 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#3B82F6]"
                    data-testid="quick-add-barcode" />
            </div>

            <button onClick={handleSubmit} disabled={saving || !name.trim()}
                className="w-full h-10 bg-[#3B82F6] text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-all"
                data-testid="quick-add-submit">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {saving ? 'Olusturuluyor...' : 'Olustur ve Sepete Ekle'}
            </button>
        </div>
    );
}
